import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, Fuel, Gauge, Calendar, CarFront, Banknote, RefreshCw, Shield, CheckCircle2, Phone, MessageCircle, ChevronRight, AlertTriangle, Info, Wrench, Clock, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_INTERVAL = 15000;

const FUEL_LABELS = {
  PETROL: "Benzina", PETROL_MILD_HYBRID: "Ibrida Mild", PETROL_FULL_HYBRID: "Ibrida Full",
  DIESEL: "Diesel", ELECTRIC: "Elettrica", LPG: "GPL", CNG: "Metano", PLUG_IN_HYBRID: "Plug-in Hybrid",
};
const TRANS_LABELS = { MAN5: "Manuale 5 marce", MAN6: "Manuale 6 marce", AUT: "Automatico", EDCT: "Automatico DCT", AUT6: "Automatico 6 marce", AUT7: "Automatico 7 marce" };
const CATEGORY_LABELS = { A: "Segmento A - Citycar", B: "Segmento B - Utilitarie", C: "Segmento C - Compatte", D: "Segmento D - Medie", E: "Segmento E - Premium", SUV: "SUV / Crossover", VAN: "Veicolo commerciale" };

function fmt(val) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

const KM_OPTIONS = [10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000, 50000, 60000, 70000, 80000];
const DURATA_OPTIONS = [24, 36, 48, 60];

const SERVIZI_INCLUSI = [
  "Assicurazione RCA con Reale Mutua", "Assicurazione Personale Infortuni", "Limitazione responsabilit\u00e0 furto e incendio",
  "Limitazione responsabilit\u00e0 danni", "Manutenzione ordinaria e straordinaria", "Soccorso stradale 24/7",
  "Immatricolazione e messa su strada", "Gestione multe", "Pagamento bollo auto",
  "Tutela legale", "Centri assistenza in tutta Italia", "Portale servizi dedicato",
];

// Valori dai documenti contrattuali ufficiali Ayvens (Informazioni Precontrattuali +
// Documento "La Limitazione di Responsabilità per Danni" — versione 2025-2026).
// Le franchigie sono NEGOZIABILI in Lettera di Offerta (esempi documentati: €249, €500).
const COSTI_EXTRA = {
  kmEccedente: { value: "Indicato in Lettera di Offerta", note: "Importo €/km contrattuale, conguagliato sui km extra a fine contratto" },
  kmInferiori: { value: "Non rimborsati", note: "I km non utilizzati rispetto al pacchetto contrattuale non danno diritto a rimborso" },
  franchigiaKasko: { value: "Esempi 249 € / 500 €", note: "Limitazione responsabilità per sinistro: importo concordato in Lettera di Offerta. Possibile anche zero franchigia" },
  franchigiaFurtoIncendio: { value: "Da Lettera di Offerta", note: "Quota di responsabilità per furto/incendio definita in fase di negoziazione contratto" },
  franchigiaCristalli: { value: "Da Lettera di Offerta", note: "Cristalli in genere coperti dalla limitazione di responsabilità danni concordata" },
  esclusioniLR: { value: "Dolo, interni, pneumatici", note: "La Limitazione di Responsabilità NON copre: dolo/colpa grave, danni interni, pneumatici, riparazioni arbitrarie, danni non denunciati pre-riconsegna" },
  recesso14gg: { value: "3 mensilità di canone", note: "Recesso entro 14 giorni dalla conclusione del contratto (prima dell'immatricolazione). Esente IVA art.15 DPR 633/72" },
  recessoPostImm: { value: "4 mensilità di canone", note: "Recesso tra immatricolazione e Messa A Disposizione (MAD) del veicolo. Esente IVA art.15 DPR 633/72" },
  recessoMAD: { value: "6 mensilità di canone", note: "Recesso dopo MAD o mancato ritiro entro 15 giorni dalla disponibilità. Esente IVA art.15 DPR 633/72" },
  recessoPostConsegna: { value: "Art. 12 Cond. Generali", note: "Recesso post-consegna con preavviso 30 giorni: corrispettivo calcolato secondo art. 12 CGC" },
  speseApertura: { value: "150,00 € + IVA", note: "Spese amministrative apertura pratica una tantum, alla conclusione del primo contratto" },
  insolvenzaConto: { value: "20,00 €", note: "Penale per insolvenza c/c, esente IVA art.15 DPR 633/72" },
  rinotificaSanzioni: { value: "10,00 € + IVA", note: "Costi di gestione amministrativa per ogni verbale di infrazione gestito" },
  duplicazioneDocs: { value: "50,00 € + IVA", note: "Costi duplicazione documenti circolazione (in caso di furto/smarrimento)" },
  ripristinoChiavi: { value: "Max 250,00 € + IVA", note: "Costi ripristino chiavi/duplicati (in caso di furto/smarrimento)" },
  sinistriPlurimi: { value: "250,00 € dal 4°", note: "Penale per ogni sinistro passivo registrato dal 4° al 6°. Al 7° sinistro: risoluzione di diritto del contratto" },
};

export default function CarDetailPage() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [customKm, setCustomKm] = useState(null);
  const [customDurata, setCustomDurata] = useState(null);
  // FASE 4.2.10/4.2.11 deep scrape UI
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [selectedVariante, setSelectedVariante] = useState(null);
  const [compareData, setCompareData] = useState(null); // {delta, vincitore, altro_provider, altro_monthlyFee}

  const fetchCar = useCallback(async () => {
    try {
      // Usa il catalogo unificato per trovare auto sia Arval che Ayvens
      const resp = await fetch(`${API}/noleggio/catalog?limit=2000`);
      const data = await resp.json();
      const list = Array.isArray(data?.cars) ? data.cars : (Array.isArray(data) ? data : []);
      const found = list.find(c => String(c.id) === String(id));
      if (found) {
        // Adattatore: normalizzo campi al vecchio schema usato dalla pagina
        const kmYear = found.kmPerYear ?? 10000;
        const dur = found.durationMonths ?? found.duration ?? 48;
        const providerRaw = (found.source || found.provider || "").toLowerCase();
        const providerLabel = providerRaw === "arval" ? "Arval" : providerRaw === "ayvens" ? "Ayvens" : (found.provider || "Noleggio");
        const normalized = {
          ...found,
          mileage: found.mileage ?? found.kmTotal ?? (kmYear * (dur / 12)),
          duration: dur,
          advancedPayment: found.advancedPayment ?? found.deposit ?? 0,
          provider: providerLabel,
          imageUrl: (found.imageUrl || "").startsWith("/api/")
            ? `${process.env.REACT_APP_BACKEND_URL}${found.imageUrl}`
            : found.imageUrl,
        };
        // FASE 4.2.11.1 — Immagine principale ottimizzata (WebP smart-crop, ~30KB)
        if (normalized.imageUrl) {
          normalized.imageUrlOriginal = normalized.imageUrl;
          normalized.imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/noleggio/images/optimized?src=${encodeURIComponent(normalized.imageUrl)}&size=detail`;
        }
        // Gallery photos[]: PRIORITÀ MAX ai_gallery_local (FASE v1.14.12 Gemini AI-generated, 6 angoli)
        // poi gallery_local (WebP ottimizzato Fase D), poi gallery_urls live, poi photos legacy
        const aiGallery = (Array.isArray(normalized.ai_gallery_local) && normalized.ai_gallery_local.length > 0)
          ? normalized.ai_gallery_local.map(p => p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`)
          : null;
        const galleryPriority = aiGallery
          || ((Array.isArray(normalized.gallery_local) && normalized.gallery_local.length > 0)
            ? normalized.gallery_local.map(p => p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`)
            : (Array.isArray(normalized.gallery_urls) && normalized.gallery_urls.length > 0)
              ? normalized.gallery_urls
              : null);
        if (aiGallery) {
          normalized.is_ai_generated = true;
          // sostituisci anche l'immagine hero con la 1ª della gallery AI (vista 3/4 sinistra)
          normalized.imageUrl = aiGallery[0];
          normalized.imageUrlOriginal = normalized.imageUrl_original || aiGallery[0];
        }
        if (galleryPriority && galleryPriority.length > 0) {
          normalized.photos = galleryPriority;
          normalized.photosOriginal = [...galleryPriority];
        } else if (Array.isArray(normalized.photos) && normalized.photos.length > 0) {
          normalized.photosOriginal = [...normalized.photos];
          normalized.photos = normalized.photos.map(p =>
            p ? `${process.env.REACT_APP_BACKEND_URL}/api/images/ai-rework?src=${encodeURIComponent(p)}&context=car&size=detail` : p
          );
        }
        setCar(normalized);
        setLastUpdate(new Date());
        if (customKm === null) setCustomKm(normalized.mileage);
        if (customDurata === null) setCustomDurata(normalized.duration);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id, customKm, customDurata]);

  useEffect(() => {
    fetchCar();
    const interval = setInterval(fetchCar, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCar]);

  // Compare price endpoint: chiamato solo quando car cambia brand/model
  useEffect(() => {
    if (!car?.brand || !car?.model) return;
    let cancelled = false;
    (async () => {
      try {
        const url = `${API}/noleggio/compare?brand=${encodeURIComponent(car.brand)}&model=${encodeURIComponent(car.model)}`;
        const resp = await fetch(url);
        if (!resp.ok) return;
        const d = await resp.json();
        if (cancelled) return;
        // delta = differenza assoluta; vincitore = source pi\u00f9 economico
        const ayv = d?.ayvens?.monthlyFee;
        const arv = d?.arval?.monthlyFee;
        if (typeof ayv === "number" && typeof arv === "number" && ayv !== arv) {
          const winner = ayv < arv ? "ayvens" : "arval";
          const otherProvider = winner === "ayvens" ? "arval" : "ayvens";
          const otherFee = winner === "ayvens" ? arv : ayv;
          const delta = Math.abs(ayv - arv);
          setCompareData({ delta, vincitore: winner, altro_provider: otherProvider, altro_monthlyFee: otherFee });
        } else {
          setCompareData(null);
        }
      } catch (_) { /* silenzioso */ }
    })();
    return () => { cancelled = true; };
  }, [car?.brand, car?.model]);

  if (loading) return (
    <><Header /><main className="min-h-screen bg-[#FCFCFA] pt-20 flex items-center justify-center">
      <RefreshCw className="w-6 h-6 text-[#163321] animate-spin" />
    </main><Footer /></>
  );

  if (!car) return (
    <><Header /><main className="min-h-screen bg-[#FCFCFA] pt-20">
      <div className="max-w-2xl mx-auto px-5 py-20 text-center">
        <p className="text-sm text-[#4A5E51] mb-4">Veicolo non trovato o non pi&ugrave; disponibile.</p>
        <Link to="/noleggio" className="text-sm text-[#163321] underline">Torna alle offerte</Link>
      </div>
    </main><Footer /></>
  );

  const isCustom = customKm !== car.mileage || customDurata !== car.duration;
  const rataZeroAnticipo = car.advancedPayment > 0
    ? Math.ceil(car.monthlyFee + car.advancedPayment / car.duration)
    : car.monthlyFee;
  const isPrivati = car.target === "privati";
  const vatIncluded = !!car.vatIncluded;
  // Calcolo prezzo IVA inclusa (IVA al 22%). I prezzi listino Ayvens sono
  // SEMPRE IVA esclusa: il privato paga IVA non recuperabile sul canone
  // (costo effettivo = canone × 1,22), il P.IVA la detrae.
  const VAT_RATE = 0.22;
  const priceWithVat = vatIncluded ? car.monthlyFee : Math.round(car.monthlyFee * (1 + VAT_RATE));
  const targetLabel = isPrivati ? "Privati" : "Business / P.IVA";
  const vatLabel = vatIncluded ? "IVA inclusa" : "IVA esclusa";

  const kmIdx = KM_OPTIONS.indexOf(customKm) >= 0 ? KM_OPTIONS.indexOf(customKm) : KM_OPTIONS.findIndex(k => k >= customKm);
  const durataIdx = DURATA_OPTIONS.indexOf(customDurata) >= 0 ? DURATA_OPTIONS.indexOf(customDurata) : 0;
  const kmPercent = KM_OPTIONS.length > 1 ? ((kmIdx >= 0 ? kmIdx : 0) / (KM_OPTIONS.length - 1)) * 100 : 50;
  const durataPercent = DURATA_OPTIONS.length > 1 ? ((durataIdx >= 0 ? durataIdx : 0) / (DURATA_OPTIONS.length - 1)) * 100 : 50;

  return (
    <><Header />
    <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="car-detail-page">
      {/* Hero */}
      <div className="bg-[#F5F4F0] py-8 sm:py-12">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <div className="flex items-center justify-between mb-6">
            <Link to="/noleggio" className="inline-flex items-center gap-1.5 text-xs text-[#4A5E51] hover:text-[#163321] transition-colors" data-testid="car-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Tutte le offerte
            </Link>
            <img src="/colucci_solutions_web.png" alt="Colucci Solutions" className="h-8 sm:h-10 object-contain brightness-0" />
          </div>
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 flex flex-col items-center gap-3 w-full">
              {/* Gallery: principale + thumbnails se photos.length > 1 */}
              {(() => {
                const photos = Array.isArray(car.photos) && car.photos.length > 1 ? car.photos : null;
                const mainSrc = photos ? photos[Math.min(activePhotoIdx, photos.length - 1)] : car.imageUrl;
                return (
                  <>
                    <img
                      src={mainSrc}
                      alt={`${car.brand} ${car.model}`}
                      className="max-h-56 sm:max-h-72 object-contain"
                      data-testid="car-image"
                      loading="eager"
                      decoding="async"
                      width="800"
                      height="600"
                      onError={(e) => {
                        // Fallback su originale se l'AI rework fallisce
                        const origMain = photos
                          ? (Array.isArray(car.photosOriginal) && car.photosOriginal[Math.min(activePhotoIdx, car.photosOriginal.length - 1)])
                          : car.imageUrlOriginal;
                        if (origMain && e.currentTarget.src !== origMain) {
                          e.currentTarget.src = origMain;
                        }
                      }}
                    />
                    {photos && (
                      <div className="flex gap-2 flex-wrap justify-center" data-testid="car-gallery-thumbs">
                        {photos.map((p, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setActivePhotoIdx(i)}
                            className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${i === activePhotoIdx ? "border-[#163321]" : "border-transparent opacity-60 hover:opacity-100"}`}
                            data-testid={`car-gallery-thumb-${i}`}
                            aria-label={`Foto ${i + 1}`}
                          >
                            <img src={p} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                    {car.is_ai_generated && (
                      <p className="text-[10px] text-zinc-500 italic text-center mt-1" data-testid="ai-images-disclaimer">
                        🤖 Immagini generate con AI a scopo illustrativo (Art. 50 AI Act). Il veicolo reale può differire per allestimento e colore.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="flex-1 space-y-4 text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start flex-wrap">
                {car.highlighted && <span className="bg-[#D97B58] text-white text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">In evidenza</span>}
                {car.inStock && <span className="bg-[#163321] text-white text-[9px] font-medium px-2.5 py-1 rounded-full">Disponibile</span>}
                {car.provider === "Ayvens" ? (
                  <img src="https://www.blue2drive.com/assets/img/provider_ayvens.svg" alt="Ayvens" className="h-5 opacity-70" />
                ) : car.provider?.toLowerCase() === "arval" ? (
                  <img src="https://www.blue2drive.com/assets/img/provider_arval.svg" alt="Arval" className="h-5 opacity-70" />
                ) : (
                  <span className="text-[9px] text-[#4A5E51] bg-white px-2 py-0.5 rounded-full border border-[#E5E2DB]">{car.provider}</span>
                )}
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#0F1F14]" data-testid="car-title">{car.brand} {car.model}</h1>
              <p className="text-sm text-[#4A5E51]">{car.modelDetails}</p>
              <div className="flex items-center gap-2 justify-center md:justify-start flex-wrap">
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                    isPrivati ? "bg-white text-[#163321] border-[#163321]/30" : "bg-[#163321] text-white border-[#163321]"
                  }`}
                  data-testid="car-target-label"
                >
                  {targetLabel}
                </span>
                <span className="text-[10px] text-[#4A5E51] bg-white px-2 py-0.5 rounded-full border border-[#E5E2DB]">
                  {isPrivati ? "Pacchetto km totali" : "Pacchetto km/anno"}
                </span>
              </div>

              {/* Prezzo o Quotazione personalizzata */}
              <div className="pt-2">
                <div className="inline-block bg-white rounded-2xl p-5 border border-[#E5E2DB] shadow-sm" data-testid="car-price-box">
                  {/* Delta price vs altro provider */}
                  {compareData && (
                    <div
                      className={`mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        compareData.vincitore === (car.source || "").toLowerCase()
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-amber-50 text-amber-800 border border-amber-200"
                      }`}
                      data-testid="car-delta-badge"
                      title={`Prezzi confrontati su ${car.brand} ${car.model} tra Ayvens e Arval`}
                    >
                      {compareData.vincitore === (car.source || "").toLowerCase()
                        ? <>{"\uD83D\uDCB0"} Migliore di {fmt(compareData.delta)}/mese vs {compareData.altro_provider}</>
                        : <>{"\u26A0\uFE0F"} {fmt(compareData.delta)}/mese in pi\u00F9 vs {compareData.altro_provider}</>
                      }
                    </div>
                  )}
                  {isCustom ? (
                    <>
                      <p className="text-lg sm:text-xl font-heading font-medium text-[#D97B58]" data-testid="car-custom-label">Chiedi quotazione personalizzata</p>
                      <p className="text-[10px] text-[#4A5E51] mt-1">
                        {(customKm / 1000).toFixed(0)}.000 km &middot; {customDurata} mesi
                      </p>
                    </>
                  ) : (
                    <>
                      {car.advancedPayment > 0 ? (
                        <>
                          <p className="text-[10px] text-[#4A5E51] mb-1">Con anticipo {fmt(car.advancedPayment)}</p>
                          <p className="text-2xl sm:text-3xl font-heading font-medium text-[#163321]" data-testid="car-price">{fmt(car.monthlyFee)}<span className="text-xs font-normal text-[#4A5E51]">/mese {vatLabel}</span></p>
                          <p className="text-[10px] text-[#4A5E51] mt-1">
                            {isPrivati
                              ? <>Costo effettivo per privati ≈ <strong>{fmt(priceWithVat)}/mese</strong> (IVA 22% non recuperabile)</>
                              : <>Con IVA 22% detraibile ≈ <strong>{fmt(priceWithVat)}/mese</strong></>}
                          </p>
                          <div className="mt-2 pt-2 border-t border-[#E5E2DB]">
                            <p className="text-[10px] text-[#163321] font-medium">Zero anticipo</p>
                            <p className="text-2xl sm:text-3xl font-heading font-medium text-[#163321]" data-testid="car-price-zero">{fmt(rataZeroAnticipo)}<span className="text-xs font-normal text-[#4A5E51]">/mese</span></p>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-3xl sm:text-4xl font-heading font-medium text-[#163321]" data-testid="car-price">{fmt(car.monthlyFee)}</p>
                          <p className="text-xs text-[#4A5E51]">al mese {vatLabel}</p>
                          <p className="text-[10px] text-[#4A5E51] mt-0.5">
                            {isPrivati
                              ? <>Costo effettivo per privati ≈ <strong>{fmt(priceWithVat)}/mese</strong> (IVA 22% non recuperabile)</>
                              : <>Con IVA 22% detraibile ≈ <strong>{fmt(priceWithVat)}/mese</strong></>}
                          </p>
                          <p className="text-xs text-[#163321] font-medium mt-1">Zero anticipo</p>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-8 space-y-8">

        {/* Configuratore KM / Durata */}
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 shadow-sm" data-testid="car-configurator">
          <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-1">Personalizza la tua offerta</h2>
          <p className="text-xs text-[#4A5E51] mb-6">Modifica chilometraggio e durata per ricevere una quotazione su misura</p>

          {/* Slider KM */}
          <div className="space-y-3 mb-8">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-[#0F1F14]">Chilometraggio totale</label>
              <span className={`text-base font-heading font-medium px-3 py-1 rounded-lg ${isCustom && customKm !== car.mileage ? 'text-[#D97B58] bg-[#D97B58]/10' : 'text-[#163321] bg-[#163321]/5'}`} data-testid="car-km-value">
                {(customKm / 1000).toFixed(0)}.000 km
              </span>
            </div>
            <div className="relative pt-2 pb-1">
              <div className="absolute top-1/2 left-0 right-0 h-2 bg-[#E5E2DB] rounded-full -translate-y-1/2" />
              <div className="absolute top-1/2 left-0 h-2 bg-[#163321] rounded-full -translate-y-1/2 transition-all" style={{ width: `${kmPercent}%` }} />
              <input
                type="range"
                min="0"
                max={KM_OPTIONS.length - 1}
                value={kmIdx >= 0 ? kmIdx : 0}
                onChange={e => setCustomKm(KM_OPTIONS[parseInt(e.target.value)])}
                className="relative w-full h-2 appearance-none bg-transparent cursor-pointer z-10 slider-green"
                data-testid="car-km-slider"
              />
            </div>
            <div className="flex justify-between text-[10px] text-[#4A5E51]">
              <span>10.000 km</span>
              <span>80.000 km</span>
            </div>
          </div>

          {/* Slider Durata */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-[#0F1F14]">Durata contratto</label>
              <span className={`text-base font-heading font-medium px-3 py-1 rounded-lg ${isCustom && customDurata !== car.duration ? 'text-[#D97B58] bg-[#D97B58]/10' : 'text-[#163321] bg-[#163321]/5'}`} data-testid="car-durata-value">
                {customDurata} mesi
              </span>
            </div>
            <div className="relative pt-2 pb-1">
              <div className="absolute top-1/2 left-0 right-0 h-2 bg-[#E5E2DB] rounded-full -translate-y-1/2" />
              <div className="absolute top-1/2 left-0 h-2 bg-[#163321] rounded-full -translate-y-1/2 transition-all" style={{ width: `${durataPercent}%` }} />
              <input
                type="range"
                min="0"
                max={DURATA_OPTIONS.length - 1}
                value={durataIdx >= 0 ? durataIdx : 0}
                onChange={e => setCustomDurata(DURATA_OPTIONS[parseInt(e.target.value)])}
                className="relative w-full h-2 appearance-none bg-transparent cursor-pointer z-10 slider-green"
                data-testid="car-durata-slider"
              />
            </div>
            <div className="flex justify-between text-[10px] text-[#4A5E51]">
              {DURATA_OPTIONS.map(d => <span key={d}>{d} mesi</span>)}
            </div>
          </div>

          {/* Riepilogo configurazione */}
          <div className="mt-6 p-4 bg-[#F5F4F0] rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <p className="text-xs text-[#4A5E51]">Configurazione selezionata</p>
              <p className="text-sm font-medium text-[#0F1F14]">
                {car.brand} {car.model} &middot; {(customKm / 1000).toFixed(0)}.000 km &middot; {customDurata} mesi
              </p>
            </div>
            {isCustom ? (
              <a
                href="tel:0817611000"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#D97B58] text-white text-sm font-medium hover:bg-[#C06A48] transition-colors shrink-0"
                data-testid="car-custom-cta"
              >
                <Phone className="w-4 h-4" /> Chiedi quotazione
              </a>
            ) : (
              <div className="text-right space-y-0.5">
                {car.advancedPayment > 0 && (
                  <p className="text-xs text-[#4A5E51]">Con anticipo: {fmt(car.monthlyFee)}/mese</p>
                )}
                <p className="text-2xl font-heading font-medium text-[#163321]">
                  {fmt(car.advancedPayment > 0 ? rataZeroAnticipo : car.monthlyFee)}
                  <span className="text-xs font-normal text-[#4A5E51]">/mese {car.advancedPayment > 0 ? "(zero anticipo)" : ""}</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Specifiche */}
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 shadow-sm" data-testid="car-specs">
          <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-5">Specifiche del veicolo</h2>
          {/* Toggle varianti cambio (manuale/automatico) se disponibili */}
          {Array.isArray(car.varianti_cambio) && car.varianti_cambio.length > 1 && (
            <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="car-varianti-toggle">
              <span className="text-xs text-[#4A5E51] font-medium">Variante cambio:</span>
              {car.varianti_cambio.map((v, i) => {
                const label = v?.label || v?.transmission || TRANS_LABELS[v?.transmission] || `Variante ${i + 1}`;
                const fee = typeof v?.monthlyFee === "number" ? ` (${fmt(v.monthlyFee)}/mese)` : "";
                const active = (selectedVariante ?? 0) === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedVariante(i)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-[#163321] text-white border-[#163321]" : "bg-white text-[#0F1F14] border-[#E5E2DB] hover:border-[#163321]/40"}`}
                    data-testid={`car-variante-${i}`}
                  >
                    {label}{fee}
                  </button>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <SpecCard icon={Fuel} label="Alimentazione" value={FUEL_LABELS[car.fuelType] || car.fuel_type_human || car.fuelType} />
            <SpecCard icon={Gauge} label="Cambio" value={TRANS_LABELS[car.transmission] || (car.trasmissione ? (car.trasmissione.charAt(0).toUpperCase() + car.trasmissione.slice(1)) : car.transmission)} />
            <SpecCard icon={Calendar} label="Durata" value={`${car.duration} mesi`} />
            <SpecCard icon={CarFront} label="Chilometraggio" value={`${(car.mileage / 1000).toFixed(0)}.000 km`} />
            <SpecCard icon={Banknote} label="Anticipo" value={car.advancedPayment > 0 ? fmt(car.advancedPayment) : "Zero"} />
            {(car.cv || car.potenza_cv) && <SpecCard icon={Gauge} label="Potenza" value={`${car.cv || car.potenza_cv} CV${(car.kw || car.potenza_kw) ? ` · ${car.kw || car.potenza_kw} kW` : ""}`} data-testid="spec-card-potenza" />}
            {car.trazione && <SpecCard icon={CarFront} label="Trazione" value={car.trazione.charAt(0).toUpperCase() + car.trazione.slice(1)} />}
            {car.allestimento && <SpecCard icon={CarFront} label="Allestimento" value={car.allestimento} />}
            {/* FASE 4.2.11.11 — Card energetica deep scrape */}
            {car.co2 && <SpecCard icon={Info} label="Emissioni CO₂" value={`${car.co2} g/km`} data-testid="spec-card-co2" />}
            {car.consumi_wltp && <SpecCard icon={Fuel} label="Consumo WLTP" value={`${car.consumi_wltp} ${(car.fuelType || "").includes("ELECTRIC") || (car.fuel_type_human || "").toLowerCase().includes("elett") ? "kWh" : "l"}/100km`} data-testid="spec-card-consumi" />}
            {(car.segmento || car.category) && <SpecCard icon={CarFront} label="Segmento" value={CATEGORY_LABELS[car.segmento || car.category] || car.segmento || car.category} data-testid="spec-card-segmento" />}
          </div>
          {/* Disponibilità + Provider */}
          <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap items-center gap-3">
            {car.disponibilita === "pronta_consegna" && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold" data-testid="badge-disponibilita-pronta">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Pronta consegna
              </span>
            )}
            {car.disponibilita === "30gg" && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-semibold">⏱ Consegna ~30 giorni</span>
            )}
            {car.disponibilita === "su_ordinazione" && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">⌛ Su ordinazione</span>
            )}
            {car.source && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#163321]/10 text-[#163321] text-xs font-semibold">
                Partner: {car.source === "arval" ? "Arval" : "Ayvens"}
              </span>
            )}
          </div>
          {car.category && <p className="text-xs text-[#4A5E51] mt-4">Categoria: {CATEGORY_LABELS[car.category] || car.category}</p>}
        </div>

        {/* Servizi inclusi: usa quelli dal catalogo (scraper) se presenti, altrimenti fallback hardcoded Ayvens */}
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 shadow-sm" data-testid="car-servizi">
          <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-5">Servizi inclusi nel canone</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(Array.isArray(car.servizi_inclusi) && car.servizi_inclusi.length > 0
              ? car.servizi_inclusi
              : SERVIZI_INCLUSI
            ).map((s, idx) => (
              <div key={`${s}-${idx}`} className="flex items-center gap-2.5 p-3 bg-[#F5F4F0] rounded-xl">
                <CheckCircle2 className="w-4 h-4 text-[#163321] shrink-0" />
                <span className="text-xs text-[#0F1F14]">{s}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Penalità, franchigie e km eccedenti */}
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 shadow-sm" data-testid="car-penali-franchigie">
          <div className="flex items-start gap-3 mb-5">
            <AlertTriangle className="w-5 h-5 text-[#D97B58] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading text-lg font-medium text-[#0F1F14]">Penalità, franchigie e costi accessori</h2>
              <p className="text-xs text-[#4A5E51] mt-1">
                Valori dai <strong>documenti contrattuali ufficiali Ayvens</strong> (Informazioni Precontrattuali + Limitazione di Responsabilità Danni 2025-2026).
                Importi specifici di ogni veicolo nella <strong>Lettera di Offerta</strong> firmata.
              </p>
            </div>
          </div>

          {/* Chilometraggio */}
          <div className="space-y-3 mb-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#4A5E51] flex items-center gap-2">
              <CarFront className="w-3.5 h-3.5" /> Chilometraggio
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PenaltyRow icon={CarFront} label="Costo km eccedenti" value={COSTI_EXTRA.kmEccedente.value} note={COSTI_EXTRA.kmEccedente.note} />
              <PenaltyRow icon={CarFront} label="Km non utilizzati" value={COSTI_EXTRA.kmInferiori.value} note={COSTI_EXTRA.kmInferiori.note} />
            </div>
          </div>

          {/* Limitazione Responsabilità Danni */}
          <div className="space-y-3 mb-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#4A5E51] flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" /> Limitazione di responsabilità danni
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PenaltyRow icon={Shield} label="Quota responsabilità per sinistro" value={COSTI_EXTRA.franchigiaKasko.value} note={COSTI_EXTRA.franchigiaKasko.note} />
              <PenaltyRow icon={Shield} label="Furto / incendio" value={COSTI_EXTRA.franchigiaFurtoIncendio.value} note={COSTI_EXTRA.franchigiaFurtoIncendio.note} />
              <PenaltyRow icon={Shield} label="Cristalli" value={COSTI_EXTRA.franchigiaCristalli.value} note={COSTI_EXTRA.franchigiaCristalli.note} />
              <PenaltyRow icon={AlertTriangle} label="Esclusioni dalla LR" value={COSTI_EXTRA.esclusioniLR.value} note={COSTI_EXTRA.esclusioniLR.note} highlight />
            </div>
          </div>

          {/* Recesso anticipato */}
          <div className="space-y-3 mb-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#4A5E51] flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> Recesso anticipato
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PenaltyRow icon={Clock} label="Recesso entro 14 giorni" value={COSTI_EXTRA.recesso14gg.value} note={COSTI_EXTRA.recesso14gg.note} />
              <PenaltyRow icon={Clock} label="Post-immatricolazione" value={COSTI_EXTRA.recessoPostImm.value} note={COSTI_EXTRA.recessoPostImm.note} />
              <PenaltyRow icon={AlertTriangle} label="Mancato ritiro post-MAD" value={COSTI_EXTRA.recessoMAD.value} note={COSTI_EXTRA.recessoMAD.note} highlight />
              <PenaltyRow icon={FileText} label="Recesso dopo consegna" value={COSTI_EXTRA.recessoPostConsegna.value} note={COSTI_EXTRA.recessoPostConsegna.note} />
            </div>
          </div>

          {/* Spese amministrative e sinistri */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#4A5E51] flex items-center gap-2">
              <Wrench className="w-3.5 h-3.5" /> Spese amministrative e sinistri
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PenaltyRow icon={FileText} label="Apertura pratica" value={COSTI_EXTRA.speseApertura.value} note={COSTI_EXTRA.speseApertura.note} />
              <PenaltyRow icon={Banknote} label="Insolvenza conto corrente" value={COSTI_EXTRA.insolvenzaConto.value} note={COSTI_EXTRA.insolvenzaConto.note} />
              <PenaltyRow icon={FileText} label="Gestione sanzioni / multe" value={COSTI_EXTRA.rinotificaSanzioni.value} note={COSTI_EXTRA.rinotificaSanzioni.note} />
              <PenaltyRow icon={FileText} label="Duplicato documenti" value={COSTI_EXTRA.duplicazioneDocs.value} note={COSTI_EXTRA.duplicazioneDocs.note} />
              <PenaltyRow icon={FileText} label="Ripristino chiavi / duplicati" value={COSTI_EXTRA.ripristinoChiavi.value} note={COSTI_EXTRA.ripristinoChiavi.note} />
              <PenaltyRow icon={AlertTriangle} label="Sinistri passivi (dal 4°)" value={COSTI_EXTRA.sinistriPlurimi.value} note={COSTI_EXTRA.sinistriPlurimi.note} highlight />
            </div>
          </div>

          <div className="mt-6 p-3 bg-[#F5F4F0] rounded-xl flex items-start gap-2">
            <Info className="w-4 h-4 text-[#4A5E51] shrink-0 mt-0.5" />
            <p className="text-[10px] text-[#4A5E51] leading-relaxed">
              <strong>Nota:</strong> tutte le franchigie e quote di responsabilità sono <strong>negoziabili in Lettera di Offerta</strong>: il Cliente
              può scegliere tra opzioni proposte da Ayvens (esempi documentati €249/€500) oppure optare per copertura senza alcuna quota a suo carico
              (con incremento del canone). Le penali di recesso sono <strong>esenti IVA art. 15 DPR 633/72</strong>. Tutti i danni vanno denunciati
              tramite l'area riservata <strong>MyAyvens</strong> prima della riconsegna del veicolo.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#163321] to-[#224A32] rounded-2xl p-6 sm:p-8 text-white text-center space-y-4">
          <Shield className="w-10 h-10 mx-auto text-[#D97B58]" />
          <h2 className="font-heading text-xl font-medium">Interessato a questa offerta?</h2>
          <p className="text-sm text-white/70 max-w-md mx-auto">{car.source === "arval"
            ? "Modulo pratica credito Arval in attivazione: contattaci e un consulente preparerà la proposta su misura con tutti i dati del contratto."
            : "Richiedi una quotazione personalizzata: un nostro consulente preparerà una proposta su misura con i dati esatti del contratto entro 24h."}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Link to={car.source === "arval"
                ? `/noleggio/pratica-arval/${(car.id || "").replace(/^arval-/, "")}`
                : `/noleggio/pratica/${car.id}?km=${encodeURIComponent(customKm)}&durata=${encodeURIComponent(customDurata)}${isCustom ? "&custom=1" : ""}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#D97B58] text-white text-sm font-semibold hover:bg-[#C06A48] transition-colors shadow-lg" data-testid="car-avvia-pratica">
              {car.source === "arval" ? "Richiedi info su quest'auto" : "Richiedi Quotazione"} <ChevronRight className="w-4 h-4" />
            </Link>
            <a href="tel:0817611000" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-[#163321] text-sm font-medium hover:bg-white/90 transition-colors" data-testid="car-call">
              <Phone className="w-4 h-4" /> Chiama 081 761 1000
            </a>
          </div>
          {car.source !== "arval" && (
          <div className="pt-3 mt-3 border-t border-white/10">
            <p className="text-[11px] text-white/60 mb-2">Vuoi avviare direttamente la pratica di credito Ayvens?</p>
            <Link
              to={`/credito/pratica?car=${car.id}&brand=${encodeURIComponent(car.brand || "")}&model=${encodeURIComponent(car.model || "")}&canone=${car.monthlyFee || ""}&durata=${car.durationMonths || ""}&km=${car.kmPerYear || ""}`}
              className="inline-flex items-center gap-1.5 text-xs text-[#D97B58] hover:text-white transition-colors font-semibold underline underline-offset-2"
              data-testid="car-pratica-credito"
            >
              Compila il questionario credito (Privato / Micro-impresa / Impresa) →
            </Link>
          </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 text-[10px] text-[#4A5E51]">
          <RefreshCw className="w-3 h-3" />
          <span>Dati aggiornati ogni 15 secondi</span>
          {lastUpdate && <span>| Ultimo: {lastUpdate.toLocaleTimeString("it-IT")}</span>}
        </div>
        <p className="text-center text-[10px] text-[#4A5E51]/70 mt-3 italic" data-testid="copyright-disclaimer">
          Le immagini dei veicoli sono rielaborate per uso editoriale e commerciale nel rispetto del copyright dei partner.
        </p>
      </div>
    </main>
    <Footer />

    <style>{`
      .slider-green::-webkit-slider-thumb {
        -webkit-appearance: none; width: 22px; height: 22px; border-radius: 50%;
        background: #163321; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.2); cursor: pointer;
      }
      .slider-green::-moz-range-thumb {
        width: 22px; height: 22px; border-radius: 50%;
        background: #163321; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.2); cursor: pointer;
      }
    `}</style>
    </>
  );
}

function SpecCard({ icon: Icon, label, value }) {
  return (
    <div className="p-4 bg-[#F5F4F0] rounded-xl text-center space-y-1.5">
      <Icon className="w-5 h-5 text-[#163321] mx-auto" />
      <p className="text-[10px] text-[#4A5E51] uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-[#0F1F14]">{value}</p>
    </div>
  );
}

function PenaltyRow({ icon: Icon, label, value, note, highlight, colSpan }) {
  return (
    <div
      className={`p-3 rounded-xl border flex items-start gap-3 ${
        highlight
          ? "bg-[#D97B58]/5 border-[#D97B58]/30"
          : "bg-[#F5F4F0] border-[#E5E2DB]"
      } ${colSpan ? "sm:col-span-2" : ""}`}
    >
      <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${highlight ? "text-[#D97B58]" : "text-[#163321]"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <p className="text-xs font-medium text-[#0F1F14]">{label}</p>
          <p className={`text-xs font-bold tabular-nums ${highlight ? "text-[#D97B58]" : "text-[#163321]"}`}>{value}</p>
        </div>
        <p className="text-[10px] text-[#4A5E51] mt-1 leading-relaxed">{note}</p>
      </div>
    </div>
  );
}
